import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Edit,
  HourglassEmpty,
  Notes as NotesIcon,
  Spa,
  Verified,
  WarningAmber,
} from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { confirmChildDietary } from "apis";
import { IChildDietary } from "types";
import ChildDietaryDialog from "components/ChildDietaryDialog";

interface Props {
  childId: string | number;
  dietary?: IChildDietary | null;
  canEdit?: boolean;
  canConfirm?: boolean;
  onChanged?: (dietary: IChildDietary) => void;
}

const severityColor = (
  s?: string,
): "error" | "warning" | "info" | "default" => {
  if (s === "severe") return "error";
  if (s === "moderate") return "warning";
  if (s === "mild") return "info";
  return "default";
};

const ChildDietaryCard: React.FC<Props> = ({
  childId,
  dietary: initial,
  canEdit = true,
  canConfirm = false,
  onChanged,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [dietary, setDietary] = useState<IChildDietary | null>(initial ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const hasInfo =
    !!dietary &&
    ((dietary.allergies?.length ?? 0) > 0 ||
      !!dietary.allergyOther ||
      !!dietary.preferences ||
      !!dietary.notes);
  const confirmed = !!dietary?.confirmedByNursery;

  const handleConfirm = () => {
    setConfirming(true);
    confirmChildDietary(childId)
      .then((d) => {
        setDietary(d as IChildDietary);
        onChanged?.(d as IChildDietary);
        enqueueSnackbar(t("dietary.confirmed_toast"), { variant: "success" });
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setConfirming(false));
  };

  // Mirrors the DetailRow pattern used by the sibling cards on the child view
  // (tinted icon tile → uppercase label → value).
  const row = (
    key: string,
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    tint?: string,
  ) => (
    <Box key={key} display="flex" alignItems="flex-start" gap={1.5} py={1.5}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(tint ?? theme.palette.primary.main, 0.08),
          color: tint ?? theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.5,
            display: "block",
          }}
        >
          {label}
        </Typography>
        <Box mt={0.25}>{value}</Box>
      </Box>
    </Box>
  );

  return (
    <Card sx={{ borderRadius: 3 }} elevation={0} variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t("dietary.title")}
        </Typography>

        {!hasInfo ? (
          <Typography variant="body2" color="text.disabled" py={1}>
            {t("dietary.none")}
          </Typography>
        ) : (
          <>
            {(dietary?.allergies?.length || dietary?.allergyOther) &&
              row(
                "allergies",
                <WarningAmber sx={{ fontSize: 18 }} />,
                t("dietary.allergies"),
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {dietary?.allergies?.map((a) => (
                    <Chip
                      key={a}
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={t(`menu.allergen_list.${a}`)}
                    />
                  ))}
                  {dietary?.allergyOther && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={dietary.allergyOther}
                    />
                  )}
                  {dietary?.severity && (
                    <Chip
                      size="small"
                      color={severityColor(dietary.severity)}
                      label={t(`dietary.severity_levels.${dietary.severity}`)}
                    />
                  )}
                </Box>,
              )}

            {dietary?.preferences &&
              row(
                "preferences",
                <Spa sx={{ fontSize: 18 }} />,
                t("dietary.preferences"),
                <Typography variant="body2" fontWeight={500}>
                  {dietary.preferences}
                </Typography>,
              )}

            {dietary?.notes &&
              row(
                "notes",
                <NotesIcon sx={{ fontSize: 18 }} />,
                t("dietary.notes"),
                <Typography variant="body2" fontWeight={500}>
                  {dietary.notes}
                </Typography>,
              )}

            {row(
              "status",
              confirmed ? (
                <Verified sx={{ fontSize: 18 }} />
              ) : (
                <HourglassEmpty sx={{ fontSize: 18 }} />
              ),
              t("dietary.status"),
              <Typography
                variant="body2"
                fontWeight={600}
                color={confirmed ? "success.main" : "warning.main"}
              >
                {confirmed ? t("dietary.confirmed") : t("dietary.awaiting")}
              </Typography>,
              confirmed
                ? theme.palette.success.main
                : theme.palette.warning.main,
            )}
          </>
        )}

        {(canEdit || (canConfirm && hasInfo && !confirmed)) && (
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            {canConfirm && hasInfo && !confirmed && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={
                  confirming ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CheckCircle sx={{ fontSize: 16 }} />
                  )
                }
                disabled={confirming}
                onClick={handleConfirm}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                {t("dietary.confirm")}
              </Button>
            )}
            {canEdit && (
              <Button
                size="small"
                variant="text"
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={() => setDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {hasInfo ? t("labels.edit") : t("buttons.add")}
              </Button>
            )}
          </Box>
        )}
      </CardContent>

      {dialogOpen && (
        <ChildDietaryDialog
          open={dialogOpen}
          childId={childId}
          initial={dietary}
          onClose={(saved) => {
            setDialogOpen(false);
            if (saved) {
              setDietary(saved);
              onChanged?.(saved);
            }
          }}
        />
      )}
    </Card>
  );
};

export default ChildDietaryCard;
